/**
 * Helpers puros do módulo Financeiro.
 * Mantidos livres de React/serviços para permitir testes unitários (vitest).
 */
import { isoToBR } from '@components/dateInputUtils';
import type { AccountingPlanNode, GroupScope } from '@/types/financial';

// ─── Moeda / datas ──────────────────────────────────────────────

/** Formata um número como moeda brasileira (R$ 1.234,56). */
export const formatCurrencyBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/**
 * Converte ISO (`yyyy-MM-dd` ou timestamp completo) em `dd/MM/yyyy`.
 * Sem parsing via `Date` (não desloca o dia entre fusos horários).
 */
export const formatDateBR = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return isoToBR(iso) || iso;
};

/** Converte timestamp ISO em `dd/MM/yyyy HH:mm` (sem hora → só a data). */
export const formatDateTimeBR = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const datePart = isoToBR(iso);
  if (!datePart) return iso;
  const match = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(iso);
  return match ? `${datePart} ${match[1]}:${match[2]}` : datePart;
};

// ─── Categorias (Plano de Contas) ───────────────────────────────

export interface CategoryOption {
  value: number; // erpId
  label: string;
  /** Movement da categoria (0=Saída, 1=Entrada, 2=Outros/legado) — permite
   *  filtrar o select pelo sinal do lançamento. */
  movement?: number;
}

export interface CategoryOptionGroup {
  label: string;
  options: CategoryOption[];
}

/**
 * Constrói as opções agrupadas do select de Categoria a partir da árvore do
 * plano de contas: cada raiz vira um `<optgroup>` e as folhas (descendentes)
 * viram opções selecionáveis (value = erpId). Raiz sem filhos vira opção única
 * do próprio grupo. Nós deletados/desabilitados são ignorados.
 */
export const buildCategoryOptions = (tree: AccountingPlanNode[]): CategoryOptionGroup[] => {
  const groups: CategoryOptionGroup[] = [];

  for (const root of tree) {
    if (root.deleted || root.disabled) continue;

    const options: CategoryOption[] = [];
    const collectLeaves = (node: AccountingPlanNode) => {
      for (const child of node.children ?? []) {
        if (child.deleted || child.disabled) continue;
        if (child.children && child.children.length > 0) {
          collectLeaves(child);
        } else {
          options.push({ value: child.erpId, label: child.description, movement: child.movement });
        }
      }
    };
    collectLeaves(root);

    if (options.length === 0) {
      options.push({ value: root.erpId, label: root.description, movement: root.movement });
    }

    groups.push({ label: root.description, options });
  }

  return groups;
};

/**
 * Filtra os grupos de categoria pelo movimento do dinheiro: crédito só oferece
 * categorias de Entrada, débito só as de Saída. Movement 2 (Outros/legado) e
 * opções sem movement conhecido aparecem nos dois — esconder seria decidir por
 * um dado que o cadastro não afirmou. Grupos que ficarem vazios somem.
 */
export const filterCategoryGroupsByMovement = (
  groups: CategoryOptionGroup[],
  movement: 0 | 1,
): CategoryOptionGroup[] =>
  groups
    .map((group) => ({
      label: group.label,
      options: group.options.filter(
        (o) => o.movement == null || o.movement === movement || o.movement === 2,
      ),
    }))
    .filter((group) => group.options.length > 0);

/** Retorna o label de uma categoria (erpId) dentro das opções agrupadas. */
export const findCategoryLabel = (
  groups: CategoryOptionGroup[],
  erpId: number | null | undefined,
): string => {
  if (erpId == null) return '';
  for (const group of groups) {
    const option = group.options.find(o => o.value === erpId);
    if (option) return option.label;
  }
  return '';
};

// ─── Escopo de grupo (parcelas) ─────────────────────────────────

/** Label pt-BR do escopo de edição/exclusão de lançamentos parcelados. */
export const groupScopeLabel = (scope: GroupScope): string =>
  scope === 'group' ? 'Todas as parcelas' : 'Somente este lançamento';

// ─── Tipo de movimento (Entrada/Saída) ──────────────────────────

/** activityType: 0 = Saída, 1 = Entrada. */
export const activityTypeLabel = (activityType: number): string =>
  activityType === 1 ? 'Entrada' : 'Saída';

/** Classes Tailwind do badge de Entrada/Saída. */
export const activityTypeBadgeColor = (activityType: number): string =>
  activityType === 1
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-red-100 text-red-700';

// ─── Movimento do plano de contas ───────────────────────────────

/** movement: 0 = Saída, 1 = Entrada, 2 = Outros. */
export const movementLabel = (movement: number): string => {
  switch (movement) {
    case 0: return 'Saída';
    case 1: return 'Entrada';
    default: return 'Outros';
  }
};

/** Classes Tailwind do badge de Movimento (Saída/Entrada/Outros). */
export const movementBadgeColor = (movement: number): string => {
  switch (movement) {
    case 0: return 'bg-red-100 text-red-700';
    case 1: return 'bg-emerald-100 text-emerald-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

// ─── Margem de produtos ─────────────────────────────────────────

/** Exibe `earn` (razão preço/custo) como percentual com 2 casas (formato legado P2): 1.2345 → "123.45%". */
export const formatEarnPercent = (earn: number): string =>
  `${(earn * 100).toFixed(2)}%`;

/** Quantidade em pt-BR (1234 → "1.234"); `null` (estoque não cadastrado) vira "—". */
export const formatQuantityBR = (value: number | null | undefined): string =>
  value == null ? '—' : value.toLocaleString('pt-BR');
